import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { getPostAuthDestination } from "@/lib/postAuthRouting";
import { BottomNav } from "@/components/BottomNav";
import { RideMap, type MapRide, type MatchLine, type PortPin } from "@/components/RideMap";
import { MODALITIES, type Modality, relativeMinutes, modalityColor, paceLabel } from "@/lib/modality";
import { isCompatible } from "@/lib/geo";
import { Plus, Users, Clock, Bike } from "lucide-react";
import { WattsLogo } from "@/components/WattsLogo";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/map")({
  component: MapPage,
});

interface Ride {
  id: string;
  modality: Modality;
  pace: string;
  start_time: string;
  start_lat: number;
  start_lng: number;
  start_address: string | null;
  estimated_km: number | null;
  estimated_duration_min: number | null;
  creator_id: string;
  club_id: string | null;
}

interface ClubInfo {
  id: string;
  name: string;
  logo_url: string | null;
  signedLogoUrl: string | null;
  member_count: number;
}

function MapPage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"all" | Modality>("all");
  const [rides, setRides] = useState<Ride[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Ride[] | null>(null);
  const [participants, setParticipants] = useState<Record<string, number>>({});
  const [userPos, setUserPos] = useState<[number, number] | null>(null);
  const [center, setCenter] = useState<[number, number]>([41.3851, 2.1734]);
  const [ports, setPorts] = useState<PortPin[]>([]);
  const [clubsById, setClubsById] = useState<Record<string, ClubInfo>>({});
  const [nowTs, setNowTs] = useState<number>(() => Date.now());

  // Tick every 60s so past rides drop off client-side and badges recount.
  useEffect(() => {
    const iv = setInterval(() => setNowTs(Date.now()), 60_000);
    return () => clearInterval(iv);
  }, []);


  useEffect(() => {
    supabase
      .from("ports")
      .select(
        "id, name, slope_side, category, difficulty, length_km, elevation_gain_m, avg_gradient, altitude_m, surface, start_lat, start_lng"
      )
      .eq("is_active", true)
      .eq("is_departure_point", true)
      .not("start_lat", "is", null)
      .not("start_lng", "is", null)
      .then(({ data, error }) => {
        if (error) return;
        const mapped = (data || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          slope_side: p.slope_side,
          category: p.category,
          difficulty: p.difficulty,
          length_km: p.length_km,
          elevation_gain_m: p.elevation_gain_m,
          avg_gradient: p.avg_gradient,
          altitude_m: p.altitude_m,
          surface: p.surface,
          lat: p.start_lat,
          lng: p.start_lng,
        })) as PortPin[];
        setPorts(mapped);
      });
  }, []);

  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const c: [number, number] = [p.coords.latitude, p.coords.longitude];
        setUserPos(c);
        setCenter(c);
      },
      () => { /* silently fall back */ },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate({ to: "/auth" }); return; }
    getPostAuthDestination(user.id).then((to) => { if (to === "/onboarding") navigate({ to }); });
  }, [loading, user, navigate]);


  // Auto-expire past rides — only the current user's own rides (RLS allows creator updates only)
  useEffect(() => {
    if (!user) return;
    const expire = async () => {
      const now = new Date();
      const { data: actives } = await supabase
        .from("rides")
        .select("id, start_time, estimated_duration_min")
        .eq("status", "active")
        .eq("creator_id", user.id);
      const expired = (actives || []).filter((r: any) => {
        const end = new Date(r.start_time);
        end.setMinutes(end.getMinutes() + (r.estimated_duration_min || 120));
        return end < now;
      });
      if (expired.length) {
        await supabase
          .from("rides")
          .update({ status: "completed" })
          .in("id", expired.map((r: any) => r.id))
          .eq("creator_id", user.id);
      }
    };
    expire();
    const iv = setInterval(expire, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase
        .from("rides")
        .select("id, modality, pace, start_time, start_lat, start_lng, start_address, estimated_km, estimated_duration_min, creator_id, club_id")
        .eq("status", "active")
        .gte("start_time", new Date().toISOString())
        .order("start_time", { ascending: true })
        .limit(200);
      if (!cancelled && data) setRides(data as Ride[]);

      const { data: parts } = await supabase
        .from("ride_participants")
        .select("ride_id");
      if (!cancelled && parts) {
        const counts: Record<string, number> = {};
        parts.forEach((p: any) => { counts[p.ride_id] = (counts[p.ride_id] || 0) + 1; });
        setParticipants(counts);
      }
    };
    load();
    const channel = supabase
      .channel("rides-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "rides" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "ride_participants" }, load)
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, []);

  // Load verified club info (logo signed URL + member count) for rides with club_id
  useEffect(() => {
    let cancelled = false;
    const ids = Array.from(new Set(rides.map((r) => r.club_id).filter((x): x is string => !!x)))
      .filter((id) => !clubsById[id]);
    if (ids.length === 0) return;
    (async () => {
      const { data: clubs } = await supabase
        .from("clubs" as any)
        .select("id, name, logo_url, is_verified")
        .in("id", ids)
        .eq("is_verified", true);
      if (!clubs || cancelled) return;
      const { data: members } = await supabase
        .from("club_members" as any)
        .select("club_id, status")
        .in("club_id", ids)
        .eq("status", "approved");
      const counts: Record<string, number> = {};
      (members || []).forEach((m: any) => { counts[m.club_id] = (counts[m.club_id] || 0) + 1; });

      const entries = await Promise.all((clubs as any[]).map(async (c) => {
        let signed: string | null = null;
        if (c.logo_url) {
          const { data: s } = await supabase.storage.from("club-logos").createSignedUrl(c.logo_url, 3600);
          if (s?.signedUrl) {
            // Preload to confirm it actually loads; if not, fall back to color dot.
            signed = await new Promise<string | null>((resolve) => {
              const img = new Image();
              img.onload = () => resolve(s.signedUrl);
              img.onerror = () => resolve(null);
              img.src = s.signedUrl;
            });
          }
        }
        return [c.id, { id: c.id, name: c.name, logo_url: c.logo_url, signedLogoUrl: signed, member_count: counts[c.id] || 0 }] as const;
      }));
      if (cancelled) return;
      setClubsById((prev) => {
        const next = { ...prev };
        entries.forEach(([id, info]) => { next[id] = info; });
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [rides, clubsById]);

  const filtered = useMemo(
    () => rides.filter((r) => (filter === "all" || r.modality === filter) && new Date(r.start_time).getTime() > nowTs),
    [rides, filter, nowTs]
  );

  // Group by coordinate key (~1 m tolerance). Each group is ordered by start_time (fetch is asc).
  const groups = useMemo(() => {
    const map = new Map<string, Ride[]>();
    for (const r of filtered) {
      const key = `${r.start_lat.toFixed(5)},${r.start_lng.toFixed(5)}`;
      const arr = map.get(key);
      if (arr) arr.push(r); else map.set(key, [r]);
    }
    return map;
  }, [filtered]);

  const mapRides: MapRide[] = useMemo(() => {
    const out: MapRide[] = [];
    for (const [key, arr] of groups) {
      // Prefer a ride whose club has a resolved signed logo, so the marker keeps the club look.
      const withLogo = arr.find((r) => r.club_id && clubsById[r.club_id]?.signedLogoUrl);
      const head = withLogo ?? arr[0];
      out.push({
        id: key,
        modality: head.modality,
        lat: head.start_lat,
        lng: head.start_lng,
        clubLogoUrl: withLogo ? clubsById[withLogo.club_id!].signedLogoUrl : null,
        count: arr.length,
      });
    }
    return out;
  }, [groups, clubsById]);

  const matchLines = useMemo<MatchLine[]>(() => {
    const lines: MatchLine[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < filtered.length; i++) {
      for (let j = i + 1; j < filtered.length; j++) {
        const a = filtered[i]; const b = filtered[j];
        if (isCompatible(a as any, b as any)) {
          const key = [a.id, b.id].sort().join("|");
          if (seen.has(key)) continue;
          seen.add(key);
          lines.push({ from: [a.start_lat, a.start_lng], to: [b.start_lat, b.start_lng] });
        }
      }
    }
    return lines;
  }, [filtered]);

  const openGroupByKey = (key: string) => {
    const arr = groups.get(key);
    if (arr && arr.length) setSelectedGroup(arr);
  };

  const openCreateWithPort = (portId: string) => {
    navigate({ to: "/create", search: { portId } });
  };

  const viewPort = (portId: string) => {
    navigate({ to: "/puerto/$id", params: { id: portId } });
  };

  return (
    <div className="relative h-screen overflow-hidden bg-background">
      <div className="absolute inset-0 z-0">
        <RideMap
          rides={mapRides}
          center={center}
          userPosition={userPos}
          matchLines={matchLines}
          ports={ports}
          onPortCreateRide={openCreateWithPort}
          onPortView={viewPort}
          onMarkerClick={openGroupByKey}
        />
      </div>
      {matchLines.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 top-20 z-40 flex justify-center px-4">
          <div className="rounded-full border border-primary/40 bg-[#1a1a1a]/90 px-3 py-1 text-[11px] text-primary backdrop-blur-md">
            {t("map.compatibleHint")}
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="fixed inset-x-0 top-0 z-50 px-3 pt-[max(env(safe-area-inset-top),0.5rem)] pb-2">
        <div className="rounded-2xl border border-border bg-[#0f0f0f]/90 px-4 py-2.5 backdrop-blur-md">
          <div className="flex min-w-0 items-center gap-3" style={{ minHeight: 56 }}>
            <WattsLogo width={80} />
            <span
              className="font-sans text-[12px] text-[#f5f5f5] truncate"
              style={{ fontFamily: "'DM Sans', system-ui, sans-serif", opacity: 0.7 }}
            >
              {t("map.tagline")}
            </span>
          </div>
          <div className="mt-2.5 flex justify-center gap-1.5 overflow-x-auto">
            {MODALITIES.map((m) => (
              <Chip
                key={m.value}
                active={filter === m.value}
                color={modalityColor(m.value)}
                onClick={() => setFilter(filter === m.value ? "all" : m.value)}
              >
                {t(`map.filters.${m.value}` as any)}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      {/* Active count / empty state */}
      {!selectedGroup && (
        <div className="fixed inset-x-0 bottom-40 z-40 flex justify-center px-4">
          {filtered.length === 0 ? (
            <div className="w-full max-w-sm rounded-3xl border border-border bg-[#1a1a1a]/95 p-6 text-center backdrop-blur-md fade-in">
              <Bike className="mx-auto h-10 w-10 text-primary" strokeWidth={1.8} />
              <p className="mt-3 text-sm text-white/70">{t("map.noRides")}</p>
              <button
                onClick={() => navigate({ to: "/create" })}
                className="press-scale mt-4 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-primary-foreground"
              >
                {t("map.createFirst")}
              </button>
            </div>
          ) : (
            <div className="glass-panel rounded-full px-4 py-2 text-sm">
              <span className="text-muted-foreground">{t("map.activeRides", { count: filtered.length })}</span>
            </div>
          )}
        </div>
      )}

      {/* FAB */}
      <div className="fixed right-6 bottom-24 z-50">
        <button
          onClick={() => navigate({ to: "/create" })}
          className={`press-scale relative flex h-14 w-14 items-center justify-center rounded-full bg-[#F5C518] text-black shadow-[0_12px_28px_-6px_oklch(0.92_0.18_102/0.6)] ${
            filtered.length === 0 ? "fab-pulse" : ""
          }`}
          aria-label="Crear salida"
        >
          <Plus className="h-7 w-7" strokeWidth={2.5} />
        </button>
      </div>

      {/* Bottom sheet */}
      {selectedGroup && selectedGroup.length > 0 && (() => {
        const single = selectedGroup.length === 1 ? selectedGroup[0] : null;
        const clubId = selectedGroup.find((r) => r.club_id)?.club_id ?? null;
        const club = clubId ? clubsById[clubId] : null;
        return (
          <div
            className="fixed inset-x-0 z-40 sheet-up"
            style={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}
          >
            <div className="max-h-[60vh] overflow-y-auto rounded-t-3xl border-t border-x border-border bg-[#1a1a1a] px-5 pt-3 pb-6 shadow-[0_-12px_32px_rgba(0,0,0,0.5)]">
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/20" />
              <div className="relative">
                <button
                  onClick={() => setSelectedGroup(null)}
                  className="absolute right-0 top-0 text-xs text-white/50"
                >
                  {t("map.close")}
                </button>
                {club && (
                  <div className="mb-3 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                    {club.signedLogoUrl ? (
                      <img
                        src={club.signedLogoUrl}
                        alt={club.name}
                        className="h-10 w-10 rounded-full object-cover"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-sm font-bold text-white/70">
                        {club.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-white">{club.name}</div>
                      <div className="text-xs text-white/50">{club.member_count} miembros</div>
                    </div>
                  </div>
                )}
                {single ? (
                  <>
                    <div className="flex items-center gap-2">
                      <ModalityBadge modality={single.modality} />
                      <PaceBadge pace={single.pace} />
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-primary" />
                      <span className="font-medium">{relativeMinutes(single.start_time)}</span>
                      <span className="text-white/40">·</span>
                      <span className="truncate text-white/60">{single.start_address ?? t("map.departurePoint")}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-sm text-white/60">
                      {single.estimated_km && <span>{single.estimated_km} km</span>}
                      {single.estimated_duration_min && <span>{single.estimated_duration_min} min</span>}
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" /> {participants[single.id] ?? 0}
                      </span>
                    </div>
                    <button
                      onClick={() => navigate({ to: "/ride/$rideId", params: { rideId: single.id } })}
                      className="press-scale mt-4 w-full rounded-xl bg-primary py-4 font-bold text-primary-foreground"
                    >
                      {t("map.viewRide")}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="mb-2 flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-primary" />
                      <span className="truncate text-white/70">{selectedGroup[0].start_address ?? t("map.departurePoint")}</span>
                      <span className="ml-auto text-xs text-white/50">{selectedGroup.length} salidas</span>
                    </div>
                    <ul className="flex flex-col gap-2">
                      {selectedGroup.map((r) => (
                        <li key={r.id}>
                          <button
                            onClick={() => navigate({ to: "/ride/$rideId", params: { rideId: r.id } })}
                            className="press-scale w-full rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-left transition-colors hover:bg-white/[0.06]"
                          >
                            <div className="flex items-center gap-2">
                              <ModalityBadge modality={r.modality} />
                              <PaceBadge pace={r.pace} />
                              <span className="ml-auto flex items-center gap-1 text-xs text-white/60">
                                <Users className="h-3.5 w-3.5" /> {participants[r.id] ?? 0}
                              </span>
                            </div>
                            <div className="mt-2 flex items-center gap-2 text-sm">
                              <Clock className="h-4 w-4 text-primary" />
                              <span className="font-medium">{relativeMinutes(r.start_time)}</span>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      <BottomNav />
    </div>
  );
}


function Chip({ active, onClick, color, children }: { active: boolean; onClick: () => void; color?: string; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors"
      style={
        active && color
          ? { background: `${color}1a`, color, border: `1px solid ${color}` }
          : { background: "#1a1a1a", color: "#888888", border: "1px solid #2a2a2a" }
      }
    >
      {children}
    </button>
  );
}

function ModalityBadge({ modality }: { modality: Modality }) {
  const styles: Record<Modality, string> = {
    road: "bg-[#F5C518]/10 text-[#F5C518] border border-[#F5C518]/30",
    gravel: "bg-[#e07b3a]/10 text-[#e07b3a] border border-[#e07b3a]/30",
    mtb: "bg-[#4caf7d]/10 text-[#4caf7d] border border-[#4caf7d]/30",
    ebike: "bg-[#3b82f6]/10 text-[#3b82f6] border border-[#3b82f6]/30",
  };
  const label = MODALITIES.find((m) => m.value === modality)?.label;
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wider ${styles[modality]}`}>
      {label}
    </span>
  );
}

function PaceBadge({ pace }: { pace: string }) {
  const cls =
    pace === "pro"
      ? "bg-[#F5C518]/10 text-[#F5C518]"
      : pace === "medium"
      ? "bg-white/5 text-white/80"
      : "bg-white/5 text-white/60";
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${cls}`}>
      {paceLabel(pace)}
    </span>
  );
}

